import { useEffect, useMemo, useState } from "react"
import { FaCheck } from 'react-icons/fa';
import RequiredFormLabel from "../RequiredFormLabel/RequiredFormLabel";
import { Alert, Button, Card, Col, Form, Nav, Row, Spinner } from 'react-bootstrap';
import i18next from "i18next";
import AccountSmallPreview from "../AccountSmallPreview/AccountSmallPreview";
import CardPlaceholder from "../CardPlaceholder/CardPlaceholder";
import { StellantisQuoteRequestDTO } from "src/api/models/StellantisDTO";
import _ from 'lodash';
import { AccountEntitySearch, OpportunityField } from "src/api/models/AccountEntity";
import { OpportunityCreateRequestDTO, OpportunityStageEnum } from "src/api";
import MissingAccountRecordTypeAlert from "../MissingAccountRecordTypeAlert/MissingAccountRecordTypeAlert";
import { Markets, ReactSelectOption } from "src/types";
import AsyncSelect from "react-select/async";
import { useApiClient } from "src/hooks/useApiClient";
import SelectOptWithSubtitle from "../SelectOptWithSubtitle/SelectOptWithSubtitle";
import OpportunitySmallPreview from "../Opportunities/OpportunitySmallPreview/OpportunitySmallPreview";
import VehicleCardBody from "../VehicleCardBody/VehicleCardBody";
import { dynamicTranslateOpportunityFields } from "src/utils/opportunity";
import { useAuth } from "src/hooks/useAuth";
import { isMarket } from "src/utils/markets";
import OpportunityModals from "../Opportunities/OpportunityModals/OpportunityModals";
import { emailIsValid, randomString, removeDuplicates, strToFloat } from "src/utils";
import { useErrorToast } from "src/hooks/useErrorToast";
import AccountCreateRequestDTO from "src/api/models/Accounts/AccountCreateRequestDTO";
import { isAzienda } from "./stellantisUtils";
import { RecordTypeEnum } from "src/api/models/Enums";
import { getOpportunityCreationValidationSchema } from "../Opportunities/OpportunityCreation/OpportunityCreation";
import CreatableSelect from "react-select/creatable";
import { MultiValue, SingleValue } from "react-select";
import ReactQuill from "react-quill";
import { Storage } from "aws-amplify";
import { OpportunityUploadFileDTO } from "src/api/models/Opportunities/OpportunityUploadFileDTO";
import * as Constants from './stellantisConstants';
import TiassistoElement from "../TiAssisto/TiassistoElement";
import OpportunityFunnelDriveItEasyForm from "../Opportunities/OpportunityFunnelDriveItEasyForm/OpportunityFunnelDriveItEasyForm";

type Props = {
  quoteRequest: StellantisQuoteRequestDTO;
  onClose?: () => void;
  onSuccess: (result: {
    id: string;
    message: string;
    opportunityId: string;
  }) => void;
}

type FunnelStep =
  | 'account'
  | 'opportunity'
  | 'vehicle'
  | 'send_docs';

const NavItem = ({ title, text, eventKey, isValid = false }: {
  eventKey: string;
  title: string;
  text: string;
  isValid?: boolean;
}) => {
  return (
    <Nav.Item >
      <Nav.Link eventKey={eventKey} className='text-start text-muted text-success'>
        <div>
          {isValid && <span><FaCheck className='text-success'></FaCheck> &nbsp;</span>}
          <b>
            <RequiredFormLabel label={title} />
          </b>
        </div>
        <div className='text-sm'>{text}</div>
      </Nav.Link>
    </Nav.Item>
  )
}
type AccountSelectOpt = ReactSelectOption & { account: AccountEntitySearch; subtitle: string; };
export type AccountFormInitValues = AccountCreateRequestDTO & { CreatedDate: string; Id?: string; }


function StellantisOpportunityFunnel({ quoteRequest, onClose, onSuccess }: Props) {
  const [loadingState, setLoadingState] = useState({ quoteRequest: false, submitting: false });
  const [activeKey, setActiveKey] = useState<FunnelStep>('account');
  const [account, setAccount] = useState<AccountEntitySearch | undefined>();
  const { user, userReferencePartnerList, userRole } = useAuth();
  const defaultCc = user?.username ? [{ value: user.username, label: user.username }] : [];
  const [accountModalOpened, setAccountModalOpened] = useState<boolean>(false)
  const [modalType, setModalType] = useState('');
  const [activateDriveItEasy, setActivateDriveItEasy] = useState<boolean>(false)
  const { showError } = useErrorToast();
  const [funnelData, setFunnelData] = useState<any>({
    vehicle: _.pick(quoteRequest.quote, ['marca', 'modello', 'allestimento', 'images', 'alimentazione', 'cambio']),
    sendDocs: {
      hmpPdf: quoteRequest.hmpPdf,
      ...Constants.defaultMailData(defaultCc)
    },
  })

  const { apiClient, handleApiResponse } = useApiClient();
  const [accountSearchOpt, setAccountSearchOpt] = useState<AccountSelectOpt | null>(null);
  const [opportunityFields, setOpportunityFields] = useState<OpportunityField[]>([]);
  const [validation, setValidation] = useState({
    account: false,
    opportunity: false,
    vehicle: false,
    sendDocs: false,
  });

  const makeOpportunityDefaultValues = () => {
    const tipologie = opportunityFields.find(field => field?.name?.toLowerCase() === "tipologia__c")?.picklistValues || [];

    return _.cloneDeep({
      Tipologia__c: tipologie.length === 1 ? tipologie[0] : null,
      Canale__c: null,
      Noleggiatore__c: quoteRequest.quote?.noleggiatore
        ? { value: quoteRequest.quote?.noleggiatore, label: quoteRequest.quote?.noleggiatore }
        : null,
      commissions: String(quoteRequest.quote?.canone || 0),
      notes: '',
      name: '',
      driveItEasy: false,
      driverEmail: "",
      targa: "",
      telaio: "",
      dataFineContrattoDriveItEasy: "",
      nomePartner: quoteRequest.dealer
        ? { value: quoteRequest.dealer, label: quoteRequest.dealer }
        : null,
      LeadSource: null,
    });
  }

  const [opportunityValues, setOpportunityValues] = useState<any>(makeOpportunityDefaultValues());

  const computeAccountInitialValues = () => {
    const { tipoCliente, nome, cognome, ragioneSociale } = quoteRequest.quote!;

    return {
      RecordTypeId: {
        azienda: RecordTypeEnum.Azienda,
        libero_professionista: RecordTypeEnum.Libero_Professionista,
        privato: RecordTypeEnum.Privato,
      }[tipoCliente],
      Name: isAzienda(quoteRequest) ? ragioneSociale : `${nome} ${cognome}`,
      FirstName: nome,
      LastName: cognome,
      reference_partner: userReferencePartnerList?.[0]
    }
  }

  const validateFn = () => {
    const accountStatus = Boolean(funnelData.account?.Id);
    const opportunityStatus = getOpportunityCreationValidationSchema(userRole).isValidSync(opportunityValues);
    const vehicleStatus = Boolean(funnelData.vehicle?.marca && funnelData.vehicle?.modello);
    const sendDocsStatus = Boolean(funnelData.sendDocs.to && funnelData.sendDocs.body?.trim() && funnelData.sendDocs?.hmpPdf);

    const validation = {
      account: accountStatus,
      opportunity: opportunityStatus,
      vehicle: vehicleStatus,
      sendDocs: sendDocsStatus,
    }

    setValidation(validation)
  };

  const fetchOpportunityFields = async () => {
    handleApiResponse(apiClient.salesforceOpportunityController.getOpportunityFields())
      .then(opportunityFieldsRes => {
        let fields = dynamicTranslateOpportunityFields(opportunityFieldsRes)
        fields.push(
          {
            label: i18next.t('dealer'),
            name: 'nomePartner',
            picklistValues: userReferencePartnerList.map(value => ({
              active: true, label: value, value: value
            }))
          }
        )

        fields = fields.map(field => {
          if (field.name?.toLowerCase() === 'canale__c') {
            field.picklistValues = field.picklistValues.filter(item => item.value?.toLowerCase() !== 'segnalatore')
          }
          return field;
        })

        const edisonIdx = fields?.findIndex(field => field?.name === 'Offerta_Edison__c');

        if (![null, undefined, -1].includes(edisonIdx)) {
          const [edisonField] = fields.splice(edisonIdx, 1);
          edisonField && isMarket(Markets.it) && fields.push(edisonField);
        }

        const tipologie = fields.find(field => field?.name?.toLowerCase() === "tipologia__c")?.picklistValues || [];

        if (tipologie.length === 1) {
          setOpportunityValues((prev) => ({
            ...prev,
            Tipologia__c: tipologie[0]
          }))
        }
        setOpportunityFields(fields)
      })
  };

  useEffect(() => {
    validateFn();
  }, [funnelData, activeKey, opportunityValues])

  useEffect(() => {
    fetchOpportunityFields();
  }, [])

  const accountSearch = _.debounce((q: string, cb) => {
    setTimeout(() => {
      handleApiResponse(apiClient.salesforceAccountController.getAccounts({ q }))
        .then(res => {
          let list: AccountEntitySearch[] = res.data;
          const opts: AccountSelectOpt[] = list.map(el => ({
            value: el.Id,
            label: el.Name,
            account: el,
            subtitle: el?.RecordType?.Name || el.Email__c || ''
          }))
          cb(opts)
        })
    })
  }, 500)

  const cleanOpportunityValues = () => {
    setOpportunityValues(makeOpportunityDefaultValues())
  }

  const uploadHmpPdfToOpportunityBucket = async (): Promise<OpportunityUploadFileDTO> => {
    const fileName = quoteRequest.hmpPdf?.fileName!;

    const [_public, rest] = quoteRequest.hmpPdf?.key?.split('public/')!;

    const result: any = await Storage.get(rest ?? _public, {
      bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_STELLANTIS_DOCUMENTS,
      download: true
    });

    const ext = fileName.split('.').pop();
    const rawName = fileName.split('.' + ext)[0];
    const key = `opportunity_uploads/${rawName}-${randomString(10)}.${ext}`

    const blob = result.Body;
    const hmpFile = new File([blob], fileName, { type: blob.type });

    await Storage.put(key, hmpFile, {
      contentType: hmpFile.type,
      bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_SALESFORCE_IMPORT,
    });

    return ({ filename: fileName, key: `public/${key}` });
  }

  const buildPayload = async () => {
    const attachments = [await uploadHmpPdfToOpportunityBucket()];

    // @ts-ignore
    let createOpportunityPayload: OpportunityCreateRequestDTO = {
      accountId: funnelData.account!.Id,
      provvigioniPassive: strToFloat(opportunityValues?.commissions),
      channel: opportunityValues?.Canale__c?.value,
      type: opportunityValues?.Tipologia__c?.value,
      name: `HMP-${funnelData.account!.Name}-${opportunityValues?.name}`,
      dealerName: opportunityValues?.Noleggiatore__c?.value,
      emailSending: null,
      signedAttachments: attachments,
      note: opportunityValues?.notes,
      stageName: OpportunityStageEnum.proposal,
      vehicle: {
        vehicleId: null,
        customVehicle: {
          brand: quoteRequest.quote?.marca,
          model: quoteRequest.quote?.modello,
        }
      },
      driveItEasy: opportunityValues?.driveItEasy,
      driverEmail: opportunityValues?.driverEmail,
      targa: opportunityValues?.targa,
      telaio: opportunityValues?.telaio,
      // @ts-ignore
      dataFineContrattoDriveItEasy: opportunityValues?.dataFineContrattoDriveItEasy,
      isConvertedFromLead__c: false,
      nomePartner: opportunityValues?.nomePartner?.value,
      Offerta_Edison__c: opportunityValues?.Offerta_Edison__c?.value,
      salesUser: opportunityValues?.salesUser || '',
      referenceUserId: opportunityValues?.referenceUserId || '',
      backupUsers: opportunityValues?.backupUsers || [],
      LeadSource: opportunityValues?.LeadSource?.value,
    }

    if (isMarket(Markets.es)) {
      createOpportunityPayload = _.omit(createOpportunityPayload, ['Offerta_Edison__c'])
    }

    createOpportunityPayload.emailSending = {
      to: funnelData.sendDocs.to.value,
      cc: funnelData.sendDocs.cc.map(el => el.value),
      body: funnelData.sendDocs.body,
      uploadedAttachments: attachments,
    }

    return createOpportunityPayload;
  }

  const canSubmit = useMemo(() => {
    if (loadingState.submitting) return false;
    return validation.account && validation.opportunity && validation.vehicle && validation.sendDocs;
  }, [validation, loadingState]);

  const handleSubmit = async () => {
    try {
      setLoadingState((prev) => ({ ...prev, submitting: true }));
      const payload = await buildPayload();
      const res = await handleApiResponse(apiClient.stellantisControllerService.createOpportunity(quoteRequest.id, payload));
      onSuccess(res);
    } catch (error) {
      showError(error, i18next.t('error_loading_data'))
    } finally {
      setLoadingState((prev) => ({ ...prev, submitting: false }));
    }
  }

  if (loadingState.quoteRequest) {
    return <CardPlaceholder />;
  }

  return (<div>
    <Nav fill variant="tabs" activeKey={activeKey} onSelect={(k) => setActiveKey(k as FunnelStep)}>
      <NavItem
        eventKey="account"
        title={i18next.t('account')}
        isValid={validation.account}
        text={funnelData.account?.Name || i18next.t('select_one_account')}
      />
      <NavItem
        eventKey="opportunity"
        title={i18next.t('opportunity_data')}
        isValid={validation.opportunity}
        text={validation.opportunity ? i18next.t('completed') : i18next.t('complete_form')}
      />
      <NavItem
        eventKey="vehicle"
        title={i18next.t('vehicle')}
        isValid={validation.vehicle}
        text={`${funnelData.vehicle.marca} ${funnelData.vehicle.modello}`}
      />
      <NavItem
        eventKey="send_docs"
        title={i18next.t('send_quote_docs')}
        isValid={validation.sendDocs}
        text={funnelData.sendDocs.hmpPdf.fileName || ''}
      />
    </Nav>
    <div>
      <Card>
        <Card.Body className='pt-4'>

          {/* ACCOUNT CONTENT */}
          {activeKey === 'account' && (
            <>
              {(
                <div>
                  <Row>
                    {accountSearchOpt && !accountSearchOpt.account.RecordType && (
                      <Col md={12}>
                        <MissingAccountRecordTypeAlert accountId={accountSearchOpt.account?.Id} />
                      </Col>
                    )}
                    <Col md={6}>
                      <div className="mb-3">
                        <Button
                          onClick={() => {
                            setAccountModalOpened(true)
                            setModalType('account')
                          }}
                          disabled={funnelData.account !== undefined}>+ {i18next.t('create_new_account')}
                        </Button>
                      </div>
                      <Form.Group>
                        <AsyncSelect
                          menuPosition="fixed"
                          isClearable
                          placeholder={i18next.t('select_one_account')}
                          value={accountSearchOpt}
                          noOptionsMessage={() => i18next.t('type_to_search')}
                          defaultOptions={accountSearchOpt ? [accountSearchOpt] : []}
                          loadOptions={accountSearch}
                          onChange={(opt: any) => {
                            setAccountSearchOpt(opt)
                            setFunnelData({ ...funnelData, account: opt?.account?.RecordType ? opt.account : undefined })
                          }}
                          components={{
                            Option: (props: any) => (<SelectOptWithSubtitle {...props} />)
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {funnelData.account && (
                    <Row className='mt-3'>
                      <Col md={12}>
                        {<AccountSmallPreview
                          account={funnelData.account}
                          showAccountBtn={account === null}
                        />}
                      </Col>
                    </Row>
                  )}
                </div>
              )}
            </>
          )}

          {/* OPPORTUNITY CONTENT */}
          {activeKey === 'opportunity' && (
            <div>
              {funnelData.account ? (
                <Form autoComplete='off' onSubmit={_.noop}>
                  <Row>
                    <Col md={6}>
                      <div>
                        <Button
                          onClick={() => {
                            validateFn()

                            if (validation.opportunity) {
                              cleanOpportunityValues()
                              validateFn()
                            } else {
                              setAccountModalOpened(true)
                              setModalType('opportunity')
                            }
                          }}
                          disabled={funnelData.account === undefined}>
                          {validation.opportunity ? i18next.t('change') : `+ ${i18next.t('create_new')}`}
                        </Button>
                        {validation.opportunity && (
                          <Button
                            variant='secondary'
                            className='m-2'
                            onClick={() => {
                              setAccountModalOpened(true)
                              setModalType('opportunity')
                            }}>
                            {i18next.t('edit')}
                          </Button>
                        )}
                      </div>
                    </Col>
                    <Col md={12}>
                      <div>
                        {validation.opportunity && (
                          <div className='mt-3'>
                            <OpportunitySmallPreview opportunity={opportunityValues} />
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Form>
              ) :
                <Alert variant='secondary' className="mb-4">
                  {i18next.t('account_selection_required_to_proceed')}
                </Alert>
              }
            </div>
          )}

          {/* VEHICLE SECTION */}
          {activeKey === 'vehicle' && (
            <div>
              <Card className='mt-4'>
                {funnelData.vehicle && (
                  <VehicleCardBody data={{
                    dati_veicolo: {
                      marca: funnelData.vehicle.marca,
                      modello: funnelData.vehicle.modello,
                      allestimento: funnelData.vehicle.allestimento,
                      alimentazione: funnelData.vehicle.alimentazione,
                      cambio: funnelData.vehicle.cambio,
                    },
                    image_list: funnelData.vehicle.images,
                  }} />
                )}
              </Card>
              <TiassistoElement>
                <OpportunityFunnelDriveItEasyForm {...{
                  finalState: OpportunityStageEnum.proposal,
                  opportunityValues,
                  setOpportunityValues,
                  activateDriveItEasy,
                  setActivateDriveItEasy
                }}
                />
              </TiassistoElement>
            </div>
          )}

          {/* SEND DOCS CONTENT */}
          {activeKey === 'send_docs' && (
            <div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel label={i18next.t('to_recipient')} />
                    <CreatableSelect
                      isClearable
                      components={{ DropdownIndicator: null }}
                      placeholder="A"
                      noOptionsMessage={() => i18next.t('add_customer_email')}
                      value={funnelData.sendDocs.to}
                      formatCreateLabel={(i) => (emailIsValid(i) ? `Invia a ${i}` : `${i}`)}
                      onChange={(newValue: SingleValue<ReactSelectOption>) => {
                        if (!newValue || emailIsValid(newValue.value)) {
                          setFunnelData((prev) => ({ ...prev, sendDocs: { ...prev.sendDocs, to: newValue } }))
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{i18next.t('cc_recipients')}</Form.Label>
                    <CreatableSelect
                      isMulti
                      components={{ DropdownIndicator: null }}
                      placeholder="C.c."
                      noOptionsMessage={() => i18next.t('add_cc_recipients')}
                      value={funnelData.sendDocs.cc}
                      formatCreateLabel={(i) => (emailIsValid(i) ? `Aggiungi ${i}` : `${i}`)}
                      onChange={(newValue: MultiValue<ReactSelectOption>) => {
                        const filtered: ReactSelectOption[] = removeDuplicates(
                          newValue.filter((el) => emailIsValid(el.value))
                        );
                        setFunnelData((prev) => ({ ...prev, sendDocs: { ...prev.sendDocs, cc: filtered } }))
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>{i18next.t('message')}</Form.Label>
                    <ReactQuill
                      theme="snow"
                      placeholder={i18next.t('write_email')}
                      value={funnelData.sendDocs.body}
                      onChange={(body) => {
                        setFunnelData((prev) => ({ ...prev, sendDocs: { ...prev.sendDocs, body } }))
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>{i18next.t('attachments')}</Form.Label>
                    <div className="fw-bold">{quoteRequest?.hmpPdf?.fileName}</div>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>

      <div className="mt-4 text-end">
        <div>
          <Button
            variant='link'
            onClick={() => {
              onClose?.();
            }}
            disabled={loadingState.submitting}>
            {i18next.t('close')}
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={!canSubmit}>
            {loadingState.submitting ? <Spinner animation='border' size='sm' /> : i18next.t('save_and_send')}
          </Button>
        </div>
      </div>
    </div>

    <OpportunityModals
      validation={validation}
      modalType={modalType}
      setAccountSearchOpt={setAccountSearchOpt}
      setAccountModalOpened={setAccountModalOpened}
      setFunnelData={setFunnelData}
      funnelData={funnelData}
      show={accountModalOpened}
      onHide={() => setAccountModalOpened(false)}
      account={account}
      setOpportunityValues={setOpportunityValues}
      opportunityValues={opportunityValues}
      opportunityFields={opportunityFields}
      accountInitialValues={computeAccountInitialValues()}
    />
  </div>);
}

export default StellantisOpportunityFunnel;
